import styled from "styled-components";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { useDurationSubtitleFilter } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDurationSubtitleFilter";

import type { AdIntelligenceController } from "../shared/types/types";

import { DisplayAdvertisingAdPlacementWidget } from "./DisplayAdvertisingAdPlacementWidget";
import { DisplayAdvertisingDeviceBreakdownWidget } from "./DisplayAdvertisingDeviceBreakdownWidget";
import { DisplayAdvertisingFormatBreakdownWidget } from "./DisplayAdvertisingFormatBreakdownWidget";
import { useFormatBreakdown } from "./hooks/useFormatBreakdown";
import type { IFormatShare } from "./hooks/useFormatBreakdown";
import { MEDIUM_SCREEN_BREAKPOINT } from "./StyledComponents";
import type { PerformanceResponse } from "./types";

const BottomWidgetsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
`;

const ResponsiveWidgetWrapper = styled.div`
    min-width: 0;
    height: 289px;

    @media (max-width: ${MEDIUM_SCREEN_BREAKPOINT}) {
        height: 373px;
    }
`;

const EMPTY_FORMAT_SHARE: IFormatShare = { video: 0, banner: 0, native: 0 };

interface IAdvertiserPerformanceDeviceBreakdownWidgetProps {
    isLoading: boolean;
    spendShare: PerformanceResponse["spend"]["desktopShare"];
    impressionsShare: PerformanceResponse["impressions"]["desktopShare"];
    visitsShare: PerformanceResponse["visits"]["desktopShare"];
    placement: PerformanceResponse["placements"];
    startDate?: string;
    showBreakdownEmptyState?: boolean;
    showPlacementsEmptyState?: boolean;
    performanceError?: boolean;
    controller: AdIntelligenceController;
    hasAdIntelMetrics: boolean;
}

export const DisplayAdvertisingPerformanceBottomWidget = ({
    isLoading,
    spendShare,
    impressionsShare,
    placement,
    startDate,
    visitsShare,
    showBreakdownEmptyState,
    showPlacementsEmptyState,
    performanceError,
    controller,
    hasAdIntelMetrics,
}: IAdvertiserPerformanceDeviceBreakdownWidgetProps) => {
    const durationSubtitleFilter = useDurationSubtitleFilter(startDate);
    const combinedSubtitleForPlacementFilters = [durationSubtitleFilter];
    const combinedSubtitleForBreakdownFilters = useSubtitleFilters("date");

    const {
        data: formatBreakdownData,
        isPending: isFormatBreakdownLoading,
        error: formatBreakdownError,
    } = useFormatBreakdown();

    const firstDomainData = formatBreakdownData?.data?.[0];
    const formatShareData: {
        spendShare: IFormatShare;
        impressionsShare: IFormatShare;
        visitsShare: IFormatShare;
    } = {
        spendShare: firstDomainData?.spendShare || EMPTY_FORMAT_SHARE,
        impressionsShare: firstDomainData?.impressionsShare || EMPTY_FORMAT_SHARE,
        visitsShare: firstDomainData?.visitsShare || EMPTY_FORMAT_SHARE,
    };

    const showFormatBreakdownEmptyState =
        !isFormatBreakdownLoading &&
        (!formatShareData.spendShare || Object.values(formatShareData.spendShare).every((value) => value === 0));

    const formatBreakdownSubtitleFilter = useDurationSubtitleFilter(formatBreakdownData?.startDate);
    const combinedSubtitleForFormatBreakdown = formatBreakdownData?.isMixedDuration
        ? [formatBreakdownSubtitleFilter]
        : combinedSubtitleForBreakdownFilters;

    return (
        <BottomWidgetsContainer>
            <ResponsiveWidgetWrapper>
                <WidgetContainer
                    title={translate(`ad.intelligence.${controller}.overview.breakdown.widget.title`)}
                    tooltip={translate(`ad.intelligence.${controller}.overview.breakdown.widget.title.tooltip`)}
                    subtitleFilters={combinedSubtitleForBreakdownFilters}
                    height="100%"
                    isLoading={isLoading}
                    loaderWidth="100%"
                    showSubtitleFiltersLoadingState={false}
                    loaderHeight={98}
                >
                    <DisplayAdvertisingDeviceBreakdownWidget
                        spendShare={spendShare}
                        impressionsShare={impressionsShare}
                        visitsShare={visitsShare}
                        hasAdIntelMetrics={hasAdIntelMetrics}
                        showBreakdownEmptyState={showBreakdownEmptyState}
                        performanceError={performanceError}
                    />
                </WidgetContainer>
            </ResponsiveWidgetWrapper>
            <ResponsiveWidgetWrapper>
                <WidgetContainer
                    title={translate(`ad.intelligence.overview.format.breakdown.widget.title`)}
                    tooltip={translate(`ad.intelligence.overview.format.breakdown.widget.title.tooltip`)}
                    subtitleFilters={combinedSubtitleForFormatBreakdown}
                    height="100%"
                    isLoading={isFormatBreakdownLoading}
                    loaderWidth="100%"
                    loaderHeight={98}
                >
                    <DisplayAdvertisingFormatBreakdownWidget
                        spendShare={formatShareData.spendShare}
                        impressionsShare={formatShareData.impressionsShare}
                        visitsShare={formatShareData.visitsShare}
                        showEmptyState={showFormatBreakdownEmptyState}
                        hasError={!!formatBreakdownError}
                    />
                </WidgetContainer>
            </ResponsiveWidgetWrapper>
            <ResponsiveWidgetWrapper>
                <WidgetContainer
                    title={translate("ad.intelligence.overview.ad.placement.widget.title")}
                    tooltip={translate("ad.intelligence.overview.ad.placement.widget.title.tooltip")}
                    subtitleFilters={combinedSubtitleForPlacementFilters}
                    isLoading={isLoading}
                    loaderWidth="100%"
                    loaderHeight={98}
                    height="100%"
                >
                    <DisplayAdvertisingAdPlacementWidget
                        placement={placement}
                        showEmptyState={showPlacementsEmptyState}
                        hasError={!!performanceError}
                    />
                </WidgetContainer>
            </ResponsiveWidgetWrapper>
        </BottomWidgetsContainer>
    );
};
